import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { Fields } from 'redux-form';
import { changePatientResource } from 'redux/modules/patients';

import OrderInformationSection from 'components/workflow/OrderInformation/OrderInformationSection';
import PrescriberCreateEditDialog from 'components/settings/PrescriberCreateEditDialog';
import PrescribersSearchDialog from 'components/settings/PrescribersSearchDialog';
import PrescribersAddDialog from 'components/patient/PatientProfile/Prescribers/PrescribersAddDialog';

import * as normalize from 'utils/normalize';
import formatPrescriberName from 'utils/formatPrescriberName';

import OrderInformationPrescriberSelection from './OrderInformationPrescriberSelection';
import OrderInformationPrescriberEmpty from './OrderInformationPrescriberEmpty';

class OrderInformationPrescriber extends Component {
  constructor(props) {
    super(props);

    this.state = {
      addDialogOpen: false,
      searchDialogOpen: false,
      createDialogOpen: false,
      createDialogInitialValues: null,
    };

    this.onAddButtonClick = this.onAddButtonClick.bind(this);
    this.onAddDialogClose = this.onAddDialogClose.bind(this);
    this.onCreateButtonClick = this.onCreateButtonClick.bind(this);
    this.onCreateDialogClose = this.onCreateDialogClose.bind(this);
    this.onSearchDialogClose = this.onSearchDialogClose.bind(this);
    this.onPrescriberSelect = this.onPrescriberSelect.bind(this);
    this.onPrescriberSearchButtonClick = this.onPrescriberSearchButtonClick.bind(this);
  }

  onAddButtonClick() {
    this.setState({ addDialogOpen: true });
  }

  onAddDialogClose() {
    this.setState({ addDialogOpen: false });
  }

  onCreateButtonClick() {
    this.setState({
      addDialogOpen: false,
      searchDialogOpen: true,
    });
  }

  onSearchDialogClose() {
    this.setState({
      searchDialogOpen: false,
      createDialogInitialValues: null,
    });
  }

  onPrescriberSelect(values) {
    const newState = {
      searchDialogOpen: false,
      createDialogOpen: true,
    };
    if (values) {
      const primaryAddress = values.addresses[0];
      const primaryTaxonomy = values.taxonomies[0];
      const phone = primaryAddress.phone.indexOf('+1 ') !== -1 ? primaryAddress.phone.replace('+1 ', '') : primaryAddress.phone;
      newState.createDialogInitialValues = {
        'First Name': values.first_name,
        'Last Name': values.last_name,
        NPI: values.npi.toString(),
        LIC: primaryTaxonomy.license,
        Phone: normalize.phone(phone),
        'Name Prefix': values.name_prefix ? values.name_prefix : '',
        'Name Suffix': (values.name_suffix && values.name_suffix !== '--') ? values.name_suffix : '',
        Credential: values.credential ? values.credential : '',
        address: {
          Street: primaryAddress.address,
          City: primaryAddress.city,
          State: primaryAddress.state,
          'Zip Code': primaryAddress.postal_code.slice(0, 5),
        },
        taxonomies: values.taxonomies.map(item => ({
          Code: item.code,
          Description: item.desc,
          'Is Primary': item.primary,
          ...item.state ? { State: item.state } : {},
          ...item.license ? { License: item.license } : {},
        })),
      };
    } else {
      newState.createDialogInitialValues = null;
    }
    this.setState(newState);
  }

  onCreateDialogClose() {
    this.setState({
      createDialogOpen: false,
      createDialogInitialValues: null,
    });
  }

  onPrescriberSearchButtonClick() {
    this.setState({
      createDialogOpen: false,
      searchDialogOpen: true,
    });
  }

  render() {
    const { Patient, actions, change, formValues, disabled } = this.props;
    const {
      addDialogOpen,
      searchDialogOpen,
      createDialogOpen,
      createDialogInitialValues,
    } = this.state;
    return (
      <OrderInformationSection>
        {Patient.Prescriber.length > 0 ? (
          <Fields
            names={[
              'Prescriber Name',
              'Primary Insurance ID',
            ]}
            component={OrderInformationPrescriberSelection}
            Prescriber={Patient.Prescriber}
            onAddButtonClick={this.onAddButtonClick}
            change={change}
            disabled={disabled}
          />
        ) : (
          <Fields
            names={[
              'Prescriber ID',
              'Primary Insurance ID',
            ]}
            component={OrderInformationPrescriberEmpty}
            onAddButtonClick={this.onAddButtonClick}
            disabled={disabled}
          />
        )}
        <PrescribersAddDialog
          open={addDialogOpen}
          onClose={this.onAddDialogClose}
          onCreateButtonClick={this.onCreateButtonClick}
          onSubmitSuccess={(values) => {
            this.onAddDialogClose();
            new Promise((resolve, reject) => {
              actions.changePatientResource({
                'Record Number': Patient['Record Number'],
                'Prescriber ID': [
                  values.ID,
                  ...Patient.Prescriber.map(item => item.ID),  
                ],
                resolve,
                reject,
              });
            }).then(() => {
              change('Prescriber Name', values.Name);
              change('Selected Prescriber Name', values.Name);
              change('Prescriber ID', values.ID);
            });
          }}
          formValues={formValues}
        />
        <PrescriberCreateEditDialog
          form="prescriberCreate"
          open={createDialogOpen}
          onClose={this.onCreateDialogClose}
          onSubmitSuccess={values => (
            new Promise((resolve, reject) => {
              actions.changePatientResource({
                'Record Number': Patient['Record Number'],
                'Prescriber ID': [
                  values.ID,
                  ...Patient.Prescriber.map(item => item.ID),
                ],
                resolve,
                reject,
              });
            }).then(() => {
              change('Prescriber Name', formatPrescriberName(values));
              change('Selected Prescriber Name', formatPrescriberName(values));
              change('Prescriber ID', values.ID);
            })
          )}
          {...createDialogInitialValues ? {
            initialValues: createDialogInitialValues,
          } : {}}
          onSearchButtonClick={this.onPrescriberSearchButtonClick}
        />
        <PrescribersSearchDialog
          form="prescriberCreateRegistrySearch"
          open={searchDialogOpen}
          clearOnOpen={!createDialogInitialValues}
          onClose={this.onSearchDialogClose}
          onPrescriberSelect={this.onPrescriberSelect}
        />
      </OrderInformationSection>
    );
  }
}

OrderInformationPrescriber.propTypes = {
  change: PropTypes.func.isRequired,
  Patient: PropTypes.shape({
    'Record Number': PropTypes.number.isRequired,
    Prescriber: PropTypes.arrayOf(PropTypes.shape({
      ID: PropTypes.number,
    })),
  }).isRequired,
  actions: PropTypes.shape({
    changePatientResource: PropTypes.func.isRequired,
  }).isRequired,
  disabled: PropTypes.bool,
  formValues: PropTypes.shape({
    'Primary Insurance ID': PropTypes.number,
  }),
};

OrderInformationPrescriber.defaultProps = {
  formValues: {},
  disabled: false,
};

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({ changePatientResource }, dispatch),
});

export default connect(null, mapDispatchToProps)(OrderInformationPrescriber);



// WEBPACK FOOTER //
// ./src/components/workflow/OrderInformation/OrderInformationPrescriber/OrderInformationPrescriber.js